import { useState, useEffect, useRef } from 'react';
import { useAdmin } from './AdminContext';
import { useNavigate } from 'react-router-dom';
import { REALMS } from '../../../../shared/src/realms';
import { TOPIC_CORE } from '../../../../shared/src/taxonomyV2';
import { invokeFunction, supabase } from '../../supabase';

/**
 * One-time pass that rewrites the legacy pool's difficulty and sub_topic.
 *
 * The edge function handles 320 questions per invocation so it finishes inside
 * the function timeout, which means ~25 calls for the whole pool. This panel
 * drives that loop so the operator is not clicking a button two dozen times,
 * and it reads `remaining_in_realm` from each response to know when a realm is
 * done rather than guessing at a count.
 */
function RegradePanel({ toast }: { toast: (m: string, k?: 'error' | 'success' | 'info') => void }) {
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [dryRun, setDryRun] = useState(true);
  const abort = useRef(false);

  const say = (line: string) => setLog(l => [...l.slice(-40), line]);

  const adminToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Session expired. Sign in again.');
    return session.access_token;
  };

  const run = async () => {
    setRunning(true);
    abort.current = false;
    setLog([]);
    let totalMoved = 0, totalDone = 0, totalLowFit = 0, totalJunk = 0;
    try {
      for (const realm of Object.keys(TOPIC_CORE)) {
        if (abort.current) { say('durduruldu'); break; }
        const topics = TOPIC_CORE[realm];
        say(`${realm} basliyor (${topics.length} konu)`);
        // Loop until the function reports nothing left ungraded in this realm.
        for (let guard = 0; guard < 40; guard++) {
          if (abort.current) break;
          const res: any = await invokeFunction('regrade-questions', {
            realm, topics, dry_run: dryRun, access_token: await adminToken(),
          });
          if (res?.error) { say(`  HATA: ${res.error}`); break; }
          totalMoved += res.changed_difficulty || 0;
          totalDone += res.processed || 0;
          totalLowFit += res.no_good_topic || 0;
          totalJunk += res.junk_flagged || 0;
          say(`  ${res.processed} islendi, ${res.changed_difficulty} etiket degisti, `
            + `${res.no_good_topic} konusuz, ${res.junk_flagged} cop, ${res.remaining_in_realm} kaldi`);
          if (dryRun) {
            for (const s of res.samples || []) {
              say(`    "${s.q}" ${s.was} -> ${s.now} (p=${s.p}, fit=${s.fit})${s.junk ? ' [COP]' : ''} | ${s.topic}`);
            }
            // The refusals are the useful half: they name the gaps in the taxonomy.
            for (const s of res.low_fit_samples || []) {
              say(`    KONUSUZ: "${s.q}" -> istedi: ${s.wanted} (fit=${s.fit})`);
            }
            break; // dry run inspects one batch per realm, writes nothing
          }
          if (!res.remaining_in_realm) break;
        }
      }
      say(`BITTI. ${totalDone} soru, ${totalMoved} etiket degisti, `
        + `${totalLowFit} konusuz kaldi, ${totalJunk} cop isaretlendi.`);
      if (!dryRun) toast('Re-grade tamamlandi', 'success');
    } catch (e) {
      say(`HATA: ${e instanceof Error ? e.message : String(e)}`);
      toast('Re-grade basarisiz', 'error');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="admin-card" style={{ marginTop: 16 }}>
      <h3 style={{ margin: '0 0 4px' }}>Re-grade pass</h3>
      <p style={{ fontSize: '0.9rem', color: '#ccccd4', margin: '0 0 12px', lineHeight: 1.5 }}>
        Rewrites difficulty from an estimated p-value and reassigns sub_topic from the
        v2 taxonomy. Resumable: only rows never graded are touched, so a stopped run
        continues where it left off. Run the dry pass first and read the samples.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}>
          <input type="checkbox" checked={dryRun} disabled={running}
            onChange={e => setDryRun(e.target.checked)} />
          Dry run (nothing is written)
        </label>
        <button className="admin-btn admin-btn-sm" disabled={running} onClick={run}>
          {running ? 'calisiyor...' : dryRun ? 'Dry run baslat' : 'GERCEK GECISI BASLAT'}
        </button>
        {running && (
          <button className="admin-btn admin-btn-danger admin-btn-sm"
            onClick={() => { abort.current = true; }}>
            Durdur
          </button>
        )}
      </div>
      {log.length > 0 && (
        <pre style={{
          background: '#0a0a0a', border: '1px solid #243450', padding: 10, borderRadius: 6,
          fontSize: '0.85rem', lineHeight: 1.6, maxHeight: 320, overflow: 'auto', margin: 0,
          whiteSpace: 'pre-wrap',
        }}>{log.join('\n')}</pre>
      )}
    </div>
  );
}

export function Settings() {
  const { realmStats, totalCount, refreshStats, toast } = useAdmin();
  const navigate = useNavigate();

  // ── PsycheMatrix promotions master switch ──
  // Global flag (app_settings). OFF = WOT is fully ad-free. Flip ON when
  // PsycheMatrix launches so ads go live for every player at once.
  const [pmEnabled, setPmEnabled] = useState<boolean | null>(null);
  const [pmSaving, setPmSaving] = useState(false);

  useEffect(() => {
    supabase.from('app_settings').select('value').eq('key', 'pm_promotions_enabled').maybeSingle()
      .then(({ data }) => setPmEnabled(data?.value === true));
  }, []);

  const togglePm = async () => {
    if (pmEnabled === null || pmSaving) return;
    const next = !pmEnabled;
    setPmSaving(true);
    const { error } = await supabase
      .from('app_settings')
      .upsert({ key: 'pm_promotions_enabled', value: next, updated_at: new Date().toISOString() }, { onConflict: 'key' });
    setPmSaving(false);
    if (error) { toast(error.message, 'error'); return; }
    setPmEnabled(next);
    toast(
      next
        ? 'PsycheMatrix promotions ENABLED — ads are now live for all players'
        : 'PsycheMatrix promotions DISABLED — WOT is fully ad-free',
      next ? 'success' : 'info'
    );
  };

  /**
   * Report what the database actually did, not what was asked for. These
   * buttons used to announce "Deleted 470 questions" while row-level security
   * silently discarded the statement: an RLS-filtered DELETE removes nothing
   * and still returns success. Counting the returned rows is the only way to
   * tell a real delete from a blocked one.
   */
  const deleteAndReport = async (
    run: () => PromiseLike<{ data: unknown[] | null; error: { message: string } | null }>,
    what: string,
  ) => {
    const { data, error } = await run();
    if (error) { toast(error.message, 'error'); return; }
    const n = data?.length ?? 0;
    if (n === 0) {
      toast('Nothing was deleted. The write was blocked, check the admin policy on questions.', 'error');
      return;
    }
    toast(`Deleted ${n} ${what}`, 'info');
    refreshStats();
  };

  const bulkDeleteRealm = async (realm: string) => {
    const count = realmStats[realm]?.total || 0;
    if (!window.confirm(`⚠️ DELETE ALL ${count} questions in ${realm}?\n\nThis cannot be undone.`)) return;
    await deleteAndReport(
      () => supabase.from('questions').delete().eq('realm', realm).select('id'),
      `questions from ${realm}`,
    );
  };

  const bulkDeleteByDifficulty = async (difficulty: string) => {
    let total = 0;
    Object.values(realmStats).forEach(s => {
      total += s.difficulties[difficulty as keyof typeof s.difficulties] || 0;
    });
    if (!window.confirm(`⚠️ DELETE ALL ${total} ${difficulty.toUpperCase()} questions across all realms?\n\nThis cannot be undone.`)) return;
    await deleteAndReport(
      () => supabase.from('questions').delete().eq('difficulty', difficulty).select('id'),
      `${difficulty} questions`,
    );
  };

  return (
    <div>
      <h2 className="admin-section-title" style={{ marginBottom: '20px' }}>SETTINGS</h2>

      {/* PsycheMatrix Promotions — master ad switch */}
      <div className="admin-card" style={{ marginBottom: '24px', borderColor: pmEnabled ? '#c4973a55' : '#3E4E70' }}>
        <h3 className="admin-card-title" style={{ marginBottom: '8px' }}>PsycheMatrix Promotions</h3>
        <p style={{ fontSize: '0.8rem', color: '#ccccd4', marginBottom: '16px', lineHeight: 1.5 }}>
          Master switch for every PsycheMatrix ad in WOT — the desktop sidebars and the
          "go deeper" call-to-action on the results screen. Keep this <strong>OFF</strong> until
          PsycheMatrix launches; the game stays completely ad-free. The cognitive snapshot
          (Recall / Speed / Consistency + archetype) always stays visible either way.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <button
            onClick={togglePm}
            disabled={pmEnabled === null || pmSaving}
            className="admin-btn"
            style={{
              minWidth: 130,
              minHeight: 44,
              background: pmEnabled ? 'linear-gradient(135deg, #c4973a, #a07c2e)' : '#243450',
              color: pmEnabled ? '#000' : '#ccccd4',
              border: pmEnabled ? 'none' : '1px solid #3E4E70',
              fontWeight: 800,
              letterSpacing: '0.08em',
              cursor: pmEnabled === null || pmSaving ? 'default' : 'pointer',
            }}
          >
            {pmEnabled === null ? '…' : pmSaving ? 'SAVING…' : pmEnabled ? '● ADS ON' : '○ ADS OFF'}
          </button>
          <span style={{ fontSize: '0.8rem', color: pmEnabled ? '#c4973a' : '#bebec6', fontFamily: 'var(--font-mono)' }}>
            {pmEnabled === null
              ? 'loading…'
              : pmEnabled
                ? 'Ads are LIVE for all players'
                : 'WOT is ad-free'}
          </span>
        </div>
      </div>

      {/* System Info */}
      <div className="admin-card" style={{ marginBottom: '24px' }}>
        <h3 className="admin-card-title" style={{ marginBottom: '16px' }}>System Info</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#bebec6' }}>
            <span>Total Questions</span>
            <span style={{ color: '#c4973a' }}>{totalCount.toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#bebec6' }}>
            <span>Active Realms</span>
            <span style={{ color: '#14b8a6' }}>{Object.keys(realmStats).filter(k => realmStats[k].total > 0).length} / {Object.keys(REALMS).length}</span>
          </div>
          {Object.entries(REALMS).map(([key, val]) => {
            const s = realmStats[key];
            return (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', color: '#444' }}>
                <span style={{ color: val.color }}>{key}</span>
                <span>{s?.total || 0} &nbsp;
                  <span style={{ color: '#10b981', fontSize: '0.85rem' }}>E:{s?.difficulties.easy || 0}</span> &nbsp;
                  <span style={{ color: '#f59e0b', fontSize: '0.85rem' }}>M:{s?.difficulties.medium || 0}</span> &nbsp;
                  <span style={{ color: '#ef4444', fontSize: '0.85rem' }}>H:{s?.difficulties.hard || 0}</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Danger Zone */}
      <div className="admin-card" style={{ borderColor: '#ef444433' }}>
        <h3 className="admin-card-title" style={{ color: '#ef4444', marginBottom: '16px' }}>⚠️ Danger Zone</h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ fontSize: '0.8rem', color: '#bebec6', marginBottom: '8px' }}>Delete by realm:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {Object.entries(REALMS).map(([key, val]) => (
              <button key={key} className="admin-btn admin-btn-danger admin-btn-sm"
                disabled={!realmStats[key]?.total}
                onClick={() => bulkDeleteRealm(key)}>
                🗑️ {key} ({realmStats[key]?.total || 0})
              </button>
            ))}
          </div>

          <div style={{ borderTop: '1px solid #243450', paddingTop: '12px', marginTop: '8px' }}>
            <div style={{ fontSize: '0.8rem', color: '#bebec6', marginBottom: '8px' }}>Delete by difficulty:</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {['easy', 'medium', 'hard'].map(d => {
                let total = 0;
                Object.values(realmStats).forEach(s => { total += s.difficulties[d as keyof typeof s.difficulties] || 0; });
                return (
                  <button key={d} className="admin-btn admin-btn-danger admin-btn-sm" disabled={total === 0}
                    onClick={() => bulkDeleteByDifficulty(d)}>
                    🗑️ ALL {d.toUpperCase()} ({total})
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <RegradePanel toast={toast} />
      <AuditPanel toast={toast} />
    </div>
  );
}

/**
 * The fact-checker. Separate from the re-grade pass because it answers a
 * different question: not "how hard is this" but "is this even right".
 */
function AuditPanel({ toast }: { toast: (m: string, k?: 'error' | 'success' | 'info') => void }) {
  const [running, setRunning] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [queueOnly, setQueueOnly] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const abort = useRef(false);
  const say = (l: string) => setLog(x => [...x.slice(-60), l]);

  const run = async () => {
    setRunning(true); abort.current = false; setLog([]);
    let checked = 0, flagged = 0;
    const totals: Record<string, number> = {};
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Session expired. Sign in again.');
      for (const realm of Object.keys(TOPIC_CORE)) {
        if (abort.current) { say('durduruldu'); break; }
        say(`${realm}`);
        for (let guard = 0; guard < 60; guard++) {
          if (abort.current) break;
          const res: any = await invokeFunction('audit-questions', {
            realm, scope: queueOnly ? 'queue' : 'all', dry_run: dryRun,
            access_token: session.access_token,
          });
          if (res?.error) { say(`  HATA: ${res.error}`); break; }
          checked += res.checked || 0;
          flagged += res.flagged || 0;
          for (const [k, v] of Object.entries(res.by_flag || {})) totals[k] = (totals[k] || 0) + Number(v);
          say(`  ${res.checked} kontrol, ${res.flagged} isaretli `
            + `(${res.pulled_from_play} oyundan cikti), ${res.remaining} kaldi`);
          for (const s of res.samples || []) {
            say(`    [${s.flags.join(',')}] "${s.q}" -> ${s.marked}`);
            if (s.note) say(`      ${s.note}`);
          }
          if (dryRun || !res.remaining) break;
        }
      }
      say(`BITTI. ${checked} kontrol, ${flagged} isaretli. `
        + Object.entries(totals).map(([k, v]) => `${k}:${v}`).join(' '));
      if (!dryRun) toast('Denetim tamamlandi', 'success');
    } catch (e) {
      say(`HATA: ${e instanceof Error ? e.message : String(e)}`);
      toast('Denetim basarisiz', 'error');
    } finally { setRunning(false); }
  };

  return (
    <div className="admin-card" style={{ marginTop: 16 }}>
      <h3 style={{ margin: '0 0 4px' }}>Answer audit</h3>
      <p style={{ fontSize: '0.9rem', color: '#ccccd4', margin: '0 0 12px', lineHeight: 1.5 }}>
        Checks whether the marked answer is actually correct, whether two options are
        both defensible, whether the fact expires, and whether the explanation agrees.
        Flagged questions leave play and enter the review queue. Nothing is deleted.
      </p>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}>
          <input type="checkbox" checked={dryRun} disabled={running} onChange={e => setDryRun(e.target.checked)} />
          Dry run
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}>
          <input type="checkbox" checked={queueOnly} disabled={running} onChange={e => setQueueOnly(e.target.checked)} />
          Sadece review kuyrugu
        </label>
        <button className="admin-btn admin-btn-sm" disabled={running} onClick={run}>
          {running ? 'calisiyor...' : dryRun ? 'Dry run baslat' : 'DENETIMI BASLAT'}
        </button>
        {running && (
          <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => { abort.current = true; }}>
            Durdur
          </button>
        )}
      </div>
      {log.length > 0 && (
        <pre style={{
          background: '#0a0a0a', border: '1px solid #243450', padding: 10, borderRadius: 6,
          fontSize: '0.85rem', lineHeight: 1.6, maxHeight: 320, overflow: 'auto', margin: 0,
          whiteSpace: 'pre-wrap',
        }}>{log.join('\n')}</pre>
      )}
    </div>
  );
}
